import { call, put, takeLatest } from "redux-saga/effects";
import axiosInstance from "../../instance/AxiosInstance";
import {
  FETCH_VENDORS_REQUEST,
  ADD_VENDOR_REQUEST,
  UPDATE_VENDOR_REQUEST,
  DELETE_VENDOR_REQUEST,
  FETCH_VENDOR_DETAILS_REQUEST,
  UPDATE_VENDOR_APPROVAL_STATUS_REQUEST,
  FETCH_ALL_VENDORS_REQUEST,
  FETCH_CUSTOMERS_BY_VENDOR_REQUEST,
} from "../types/vendorTypes";
import {
  fetchVendorsSuccess,
  fetchVendorsFailure,
  addVendorSuccess,
  addVendorFailure,
  updateVendorSuccess,
  updateVendorFailure,
  deleteVendorSuccess,
  deleteVendorFailure,
  fetchVendorDetailsSuccess,
  fetchVendorDetailsFailure,
  updateVendorApprovalStatusSuccess,
  updateVendorApprovalStatusFailure,
  fetchAllVendorSuccess,
  fetchAllVendorFailure,
  fetchCustomersByVendorSuccess,
  fetchCustomersByVendorFailure,
} from "../actions/vendorActions";

export const updateVendorStatusAPI = async (payload) => {
  const response = await axiosInstance.put(`/admin/vendorconfirmed`, payload);
  return response.data;
};

function* handleFetchVendors(action) {
  const { payload } = action;

  if (!payload) {
    yield put(fetchVendorsFailure("Invalid payload"));
    return;
  }

  try {
    yield put(fetchVendorsSuccess({ data: [], totalPages: 0 }));

    const params = {
      approove_status: payload.approove_status,
    };
    if (payload.search && !payload.search_by) {
      params.pagination_required = false;
    }
    if (payload.search) {
      params.pagination_required = true;
      params.pagesize = payload.pagesize;
      params.pageNo = payload.pageNo;
      params.search = payload.search;

      if (payload.search_by && payload.search_by.trim() !== "") {
        params.search_by = payload.search_by;
      }
    } else {
      if (!payload.pagesize || !payload.pageNo) {
        yield put(fetchVendorsFailure("Invalid pagination parameters"));
        return;
      }
      params.pagination_required = true;
      params.pagesize = payload.pagesize;
      params.pageNo = payload.pageNo;
    }

    const response = yield call(axiosInstance.get, `/vendors/list`, { params });

    if (response && response.data) {
      yield put(fetchVendorsSuccess(response.data));
    } else {
      yield put(fetchVendorsFailure("Invalid response from server"));
    }
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to fetch vendors";
    yield put(fetchVendorsFailure(errorMessage));
  }
}

function* fetchAllVendors() {
  try {
    const response = yield call(axiosInstance.get, `/vendors/list`);

    yield put(fetchAllVendorSuccess(response.data));
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || "Failed to fetch vendors";
    yield put(fetchAllVendorFailure(errorMessage));
  }
}

function* handleAddVendor(action) {
  try {
    const formData = action.payload;
    const response = yield call(
      axiosInstance.post,
      "/vendors/insertvendor",
      formData
    );

    yield put(addVendorSuccess(response.data.message));
  } catch (error) {
    const errorDetails = error.response?.data || {};
    const additionalErrors = errorDetails.additionalErrors || {};
    const message = errorDetails.message || "Failed to add vendor";

    yield put(
      addVendorFailure({
        message,
        additionalErrors,
      })
    );
  }
}

function* handleUpdateVendor(action) {
  try {
    const response = yield call(
      axiosInstance.put,
      `/vendors/updatevendor/${action.payload.id}`,
      action.payload
    );
    yield put(updateVendorSuccess(response.data));
  } catch (error) {
    const errorDetails = error.response?.data || {};
    const additionalErrors = errorDetails.additionalErrors || {};
    const message = errorDetails.message || "Failed to update vendor";

    yield put(
      updateVendorFailure({
        message,
        additionalErrors,
      })
    );
  }
}

function* handleDeleteVendor(action) {
  try {
    yield call(axiosInstance.delete, `/vendors/deletevendor/${action.payload}`);
    yield put(deleteVendorSuccess(action.payload));
  } catch (error) {
    yield put(deleteVendorFailure(error.message));
  }
}

function* handleFetchVendorDetails(action) {
  try {
    const response = yield call(
      axiosInstance.get,
      `/vendors/getvendor?vendor_id=${action.payload}`
    );
    if (response.data) {
      yield put(fetchVendorDetailsSuccess(response.data.data));
    } else {
      throw new Error("Failed to fetch vendor details");
    }
  } catch (error) {
    yield put(fetchVendorDetailsFailure(error.message));
  }
}

function* handleUpdateVendorApprovalStatus(action) {
  try {
    const response = yield call(updateVendorStatusAPI, action.payload);

    yield put(updateVendorApprovalStatusSuccess(response));
  } catch (error) {
    yield put(
      updateVendorApprovalStatusFailure(
        error.response?.data?.message || "Failed to update status"
      )
    );
  }
}

function* fetchCustomersByVendorSaga(action) {
  const { payload } = action;
  const params = {
    vendor_id: payload.vendor_id,
  };
  if (payload.search) {
    params.pagination_required = true;
    params.pagesize = payload.pagesize;
    params.pageNo = payload.pageNo;
    params.search = payload.search;

    if (payload.search_by && payload.search_by.trim() !== "") {
      params.search_by = payload.search_by;
    }
  } else {
    if (!payload.pagesize || !payload.pageNo) {
      yield put(fetchCustomersByVendorFailure("Invalid pagination parameters"));
      return;
    }
    params.pagination_required = true;
    params.pagesize = payload.pagesize;
    params.pageNo = payload.pageNo;
  }
  try {
    const response = yield call(
      axiosInstance.get,
      `/customers/getcustomerbyvendor`,
      {
        params,
      }
    );

    yield put(fetchCustomersByVendorSuccess(response.data));
  } catch (error) {
    yield put(
      fetchCustomersByVendorFailure(error.message) ||
        "Failed to fetch customers"
    );
  }
}

export function* watchVendorActions() {
  yield takeLatest(
    FETCH_CUSTOMERS_BY_VENDOR_REQUEST,
    fetchCustomersByVendorSaga
  );
  yield takeLatest(FETCH_VENDORS_REQUEST, handleFetchVendors);
  yield takeLatest(ADD_VENDOR_REQUEST, handleAddVendor);
  yield takeLatest(UPDATE_VENDOR_REQUEST, handleUpdateVendor);
  yield takeLatest(DELETE_VENDOR_REQUEST, handleDeleteVendor);
  yield takeLatest(FETCH_VENDOR_DETAILS_REQUEST, handleFetchVendorDetails);
  yield takeLatest(FETCH_ALL_VENDORS_REQUEST, fetchAllVendors);
  yield takeLatest(
    UPDATE_VENDOR_APPROVAL_STATUS_REQUEST,
    handleUpdateVendorApprovalStatus
  );
}
